import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { friendlyIssueError } from "@/domains/pm/utils/issueErrors";
import type { IssueStatus, PMIssueStatus } from "@/domains/pm/types";
import { createIssueStatus, updateIssueStatus } from "@/stores/pm/repository";
import { useStatusCatalogue } from "./statusCatalogueContext";
import { slugifyStatusKey, visibleStatuses } from "./statusCatalogue";
import { nextSortOrder } from "./statusOrder";
import {
  DEFAULT_STATUS_PALETTE_ENTRY,
  paletteEntryForDot,
  STATUS_PALETTE,
  type StatusPaletteEntry,
} from "./statusPalette";

interface StatusFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The project the status belongs to. Statuses are scoped per project. */
  buildingProjectId: number;
  /** Editing an existing custom status; omitted when creating one. */
  editing?: PMIssueStatus | null;
  /** The new or updated status key, so the caller can select it immediately. */
  onSaved?: (key: IssueStatus) => void;
}

/**
 * Create or edit one custom status.
 *
 * Creating asks for a name and a colour and nothing else, because it is
 * reached from inside a status dropdown mid-task — the point is to add the
 * status you just realised you needed and carry on. The one thing that cannot
 * be inferred, whether the status means live work, is offered on the same form
 * but defaulted (new statuses are open, which "Blocked" or "Awaiting approval"
 * both want) so it can be ignored.
 */
export function StatusFormDialog({
  open,
  onOpenChange,
  buildingProjectId,
  editing,
  onSaved,
}: StatusFormDialogProps) {
  const { t } = useLanguage();
  const { catalogue } = useStatusCatalogue();
  const copy = t.pm.issues.statuses;

  const [name, setName] = useState("");
  const [palette, setPalette] = useState<StatusPaletteEntry>(DEFAULT_STATUS_PALETTE_ENTRY);
  const [saving, setSaving] = useState(false);

  // Reset on every open so a cancelled edit never leaks into the next one.
  useEffect(() => {
    if (!open) return;
    setName(editing?.label_en ?? "");
    setPalette(editing ? paletteEntryForDot(editing.dot_color) : DEFAULT_STATUS_PALETTE_ENTRY);
    setSaving(false);
  }, [open, editing]);

  const columns = useMemo(
    () => visibleStatuses(catalogue, buildingProjectId),
    [catalogue, buildingProjectId]
  );
  const takenKeys = useMemo(
    () => columns.filter((d) => d.custom && d.key !== editing?.key).map((d) => d.key),
    [columns, editing?.key]
  );

  const trimmed = name.trim();
  const canSave = trimmed.length > 0 && !saving;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      if (editing) {
        // `category` is deliberately absent: a custom status always counts as
        // open work, so there is nothing here that could change it. A row that
        // predates that rule keeps whatever it has rather than being rewritten
        // behind a rename.
        await updateIssueStatus(editing.id, {
          label_en: trimmed,
          dot_color: palette.dotColor,
          bg_color: palette.bgColor,
          text_color: palette.textColor,
        });
        onSaved?.(editing.key);
      } else {
        // The key is derived, never typed: it is what lands in pm_issues.status
        // and has to satisfy ^[a-z0-9_]{1,40}$ and avoid the built-ins.
        const key = slugifyStatusKey(trimmed, takenKeys);
        const created = await createIssueStatus({
          building_project_id: buildingProjectId,
          key,
          label_en: trimmed,
          // Always open. Only the built-in Resolved and Closed mean finished —
          // a custom status marks a stage of the work, not its end.
          category: "open",
          dot_color: palette.dotColor,
          bg_color: palette.bgColor,
          text_color: palette.textColor,
          // The end of the board. A count of the customs is not: a column
          // dragged into place is renumbered in steps of ten, so a count lands
          // the new status in front of it.
          sort_order: nextSortOrder(columns),
        });
        onSaved?.(created.key);
      }
      onOpenChange(false);
    } catch (e) {
      toast.error(friendlyIssueError(e, t));
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>{editing ? copy.editTitle : copy.newTitle}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="status-name">{copy.nameLabel}</Label>
            <Input
              id="status-name"
              value={name}
              autoFocus
              maxLength={40}
              placeholder={copy.namePlaceholder}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void save();
                }
              }}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{copy.colourLabel}</Label>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={copy.colourLabel}>
              {STATUS_PALETTE.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  role="radio"
                  aria-checked={entry.id === palette.id}
                  aria-label={entry.id}
                  onClick={() => setPalette(entry)}
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-full border transition",
                    entry.id === palette.id
                      ? "border-foreground"
                      : "border-transparent hover:border-muted-foreground/40"
                  )}
                  style={{ backgroundColor: entry.bgColor }}
                >
                  {entry.id === palette.id ? (
                    <Check className="h-3.5 w-3.5" style={{ color: entry.textColor }} />
                  ) : (
                    <span
                      className="h-2.5 w-2.5 rounded-full"
                      style={{ backgroundColor: entry.dotColor }}
                    />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* A live chip, because the palette swatches cannot show what a name
              in that colour actually reads like at badge size. */}
          <div className="space-y-1.5">
            <Label>{copy.previewLabel}</Label>
            <span
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium"
              style={{ color: palette.textColor, backgroundColor: palette.bgColor }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ backgroundColor: palette.dotColor }}
              />
              {trimmed || copy.namePlaceholder}
            </span>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t.common.cancel}
          </Button>
          <Button onClick={() => void save()} disabled={!canSave}>
            {editing ? t.common.save : copy.createAction}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
